import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, CheckCircle2, AlertCircle, SkipForward, ListChecks, Loader2, Mic, Square, RotateCcw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewQueue({ token, onClose, onDone }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [queue, setQueue] = useState(null);
  const [idx, setIdx] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Voice note recorder state
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/coach/review-queue`, auth)
      .then(({ data }) => setQueue(data.queue || []))
      .catch(() => { toast.error("Couldn't load the review queue."); setQueue([]); });
    // eslint-disable-next-line
  }, []);

  useEffect(() => { setNote(queue?.[idx]?.coach_note || ""); resetVoice(); }, [idx, queue]); // eslint-disable-line

  const current = queue?.[idx];

  const resetVoice = () => {
    stopTimer();
    setRecording(false);
    setBlob(null);
    setPreviewUrl("");
    setElapsed(0);
    chunksRef.current = [];
  };

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: "audio/webm" });
        setBlob(b);
        setPreviewUrl(URL.createObjectURL(b));
        stream.getTracks().forEach((t) => t.stop());
      };
      recRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch (e) {
      toast.error("Microphone access denied. Allow mic to record a voice note.");
    }
  };

  const stopRec = () => {
    stopTimer();
    if (recRef.current && recording) { recRef.current.stop(); setRecording(false); }
  };

  const redo = () => { setBlob(null); setPreviewUrl(""); setElapsed(0); chunksRef.current = []; };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(1, "0")}:${String(s % 60).padStart(2, "0")}`;

  const advance = () => { if (idx + 1 < (queue?.length || 0)) setIdx(idx + 1); else finish(); };
  const finish = () => { onDone && onDone(); onClose(); };

  const act = async (status) => {
    if (!current) return;
    setBusy(true);
    try {
      // Upload the voice note first (goes through the reaction endpoint which also
      // emails the player that feedback is ready), then save the review status + note.
      if (blob) {
        const fd = new FormData();
        fd.append("emoji", "");
        fd.append("voice", blob, "review-voice.webm");
        await axios.post(`${API}/coach/proofs/${current.id}/react`, fd,
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      }
      await axios.post(`${API}/coach/proofs/${current.id}/review`, { status, coach_note: note || "" }, auth);
      toast.success(status === "approved"
        ? (blob ? "Approved — voice note sent." : "Approved.")
        : (blob ? "Marked needs work — voice note sent." : "Marked needs work."));
      advance();
    } catch { toast.error("Couldn't save review."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-[#141414] border border-white/12 rounded-xl max-w-2xl w-full my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="review-queue-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ListChecks size={18} className="text-[#C41E24]" />
            <h3 className="font-headings text-xl uppercase tracking-wide">Review Queue</h3>
            {queue && queue.length > 0 && <span className="text-xs text-white/45">{idx + 1} of {queue.length}</span>}
          </div>
          <button onClick={onClose} data-testid="review-queue-close" className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        {queue === null ? (
          <div className="py-16 flex items-center justify-center text-white/50"><Loader2 size={20} className="animate-spin mr-2" /> Loading…</div>
        ) : queue.length === 0 ? (
          <div data-testid="review-queue-empty" className="py-16 text-center">
            <CheckCircle2 size={40} className="text-[#22C55E] mx-auto mb-3" />
            <p className="text-white/70 font-semibold">You're all caught up!</p>
            <p className="text-white/45 text-sm mt-1">No pending videos to review right now.</p>
          </div>
        ) : !current ? (
          <div data-testid="review-queue-done" className="py-16 text-center">
            <CheckCircle2 size={40} className="text-[#22C55E] mx-auto mb-3" />
            <p className="text-white/70 font-semibold">Queue cleared — great work, Coach!</p>
          </div>
        ) : (
          <div data-testid="review-queue-item">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <div className="font-semibold text-white">{current.player_name}</div>
                <div className="text-xs text-white/45">{current.move_label}</div>
              </div>
              <div className="h-1.5 flex-1 max-w-[140px] ml-4 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#C41E24]" style={{ width: `${(idx / queue.length) * 100}%` }} />
              </div>
            </div>
            <video key={current.id} controls autoPlay preload="metadata" className="w-full aspect-video bg-black rounded mb-3"
              src={`${API}/proofs/file/${current.id}?auth=${token}`} />

            {/* Voice note recorder — record audio feedback while watching */}
            <div className="bg-[#0d0d0d] border border-white/10 rounded-md p-3 mb-3" data-testid="review-queue-voice">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/45">Voice Feedback</span>
                {recording && <span className="text-[11px] font-mono text-[#C41E24] flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#C41E24] animate-pulse" /> {fmt(elapsed)}</span>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {!recording && !blob && (
                  <button type="button" data-testid="review-voice-record" onClick={startRec}
                    className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#141414] border border-white/15 hover:border-[#C41E24] px-3 py-2 rounded-sm transition-colors">
                    <Mic size={14} className="text-[#C41E24]" /> Record voice note
                  </button>
                )}
                {recording && (
                  <button type="button" data-testid="review-voice-stop" onClick={stopRec}
                    className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#C41E24] px-3 py-2 rounded-sm animate-pulse">
                    <Square size={12} /> Stop recording
                  </button>
                )}
                {blob && (
                  <>
                    <audio data-testid="review-voice-preview" controls src={previewUrl} className="h-9 flex-1 min-w-[160px]" />
                    <button type="button" data-testid="review-voice-redo" onClick={redo} title="Redo recording"
                      className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-white/15 text-white/70 hover:text-white hover:border-white/40 px-3 py-2 rounded-sm transition-colors">
                      <RotateCcw size={13} /> Redo
                    </button>
                    <button type="button" data-testid="review-voice-discard" onClick={redo} title="Discard" className="text-white/40 hover:text-[#C41E24] p-1.5"><Trash2 size={15} /></button>
                  </>
                )}
              </div>
              <p className="text-[11px] text-white/40 mt-2">Record your feedback while watching — hit Redo to try again before you send. It's delivered with your review.</p>
            </div>

            <textarea data-testid="review-queue-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder="Optional written feedback for the player…"
              className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-sm text-white mb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
            <div className="flex gap-2">
              <button data-testid="review-queue-approve" onClick={() => act("approved")} disabled={busy || recording}
                className="flex-1 flex items-center justify-center gap-1.5 bg-green-600/80 hover:bg-green-600 text-white text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Approve</button>
              <button data-testid="review-queue-needswork" onClick={() => act("needs_work")} disabled={busy || recording}
                className="flex-1 flex items-center justify-center gap-1.5 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <AlertCircle size={14} />} Needs Work</button>
              <button data-testid="review-queue-skip" onClick={advance} disabled={busy || recording} title="Skip for now"
                className="flex items-center justify-center gap-1.5 border border-white/15 text-white/60 hover:text-white hover:border-white/40 text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm disabled:opacity-40"><SkipForward size={14} /> Skip</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
